import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Bell, Trash2, CheckCircle, AlertCircle, Info, MessageSquare, Settings } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useOS } from '@/lib/os-context';
import { getGlassStyle, isGlassSupported } from '@/lib/glass-theme';
import { liquidPanel } from '@/lib/liquid-motion';

interface NotificationItem {
  id: string;
  type: 'success' | 'error' | 'info' | 'message';
  title: string;
  message: string;
  time: string;
  read: boolean;
}

interface NotificationCenterProps {
  isOpen: boolean;
  onClose: () => void;
}

export function NotificationCenter({ isOpen, onClose }: NotificationCenterProps) {
  const { displaySettings, effectiveColorMode } = useOS();
  const [notifications, setNotifications] = useState<NotificationItem[]>([
    {
      id: '1',
      type: 'success',
      title: 'Welcome to my Portfolio!',
      message: 'Thanks for visiting. Feel free to explore all the apps and features.',
      time: 'Just now',
      read: false,
    },
    {
      id: '2',
      type: 'info',
      title: 'Pro Tip',
      message: 'Press ⌘K or Ctrl+K to open the spotlight search from anywhere.',
      time: '2 min ago',
      read: false,
    },
    {
      id: '3',
      type: 'message',
      title: 'Check out my projects',
      message: 'I\'ve built some cool stuff! Click the Projects app to see my work.',
      time: '5 min ago',
      read: true,
    },
  ]);

  const unreadCount = notifications.filter(n => !n.read).length;

  // Glass theme support
  const isTahoe = displaySettings.theme === 'tahoe';
  const glassEnabled = isTahoe && isGlassSupported();
  const glassStyles = glassEnabled 
    ? getGlassStyle(displaySettings.glassIntensity, displaySettings.glassVariant, effectiveColorMode)
    : {};

  const markAllAsRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  const clearAll = () => {
    setNotifications([]);
  };

  const removeNotification = (id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const getIcon = (type: NotificationItem['type']) => {
    switch (type) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-400" />;
      case 'error':
        return <AlertCircle className="w-5 h-5 text-red-400" />;
      case 'info':
        return <Info className="w-5 h-5 text-blue-400" />;
      case 'message':
        return <MessageSquare className="w-5 h-5 text-purple-400" />;
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 z-9998"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          {/* Panel */}
          <motion.div
            className={cn(
              "fixed right-2 bottom-14 w-96 max-h-[70vh] rounded-xl shadow-2xl z-9999 overflow-hidden",
              isTahoe && "tahoe-liquid-panel right-3 bottom-20 rounded-[32px]",
              displaySettings.glassVariant === 'clear' && isTahoe && "tahoe-clear-glass",
              isTahoe
                ? "border-white/45"
                : glassEnabled 
                ? "border-white/15" 
                : "bg-card/95 backdrop-blur-xl border border-border"
            )}
            style={glassEnabled && !isTahoe ? {
              ...glassStyles,
              boxShadow: '0 25px 50px rgba(0, 0, 0, 0.4)',
            } : {}}
            variants={isTahoe ? liquidPanel : undefined}
            initial={isTahoe ? "hidden" : { opacity: 0, y: 20, scale: 0.95 }}
            animate={isTahoe ? "visible" : { opacity: 1, y: 0, scale: 1 }}
            exit={isTahoe ? "exit" : { opacity: 0, y: 20, scale: 0.95 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          >
            {/* Glass highlight */}
            {glassEnabled && (
              <div 
                className="absolute inset-0 rounded-xl pointer-events-none z-0"
                style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, transparent 50%)',
                }}
              />
            )}
            {/* Header */}
            <div className={cn(
              "flex items-center justify-between px-4 py-3 border-b relative z-10",
              glassEnabled ? "border-white/10" : "border-border"
            )}>
              <div className="flex items-center gap-2">
                <Bell className="w-4 h-4 text-foreground" />
                <div className="font-medium text-foreground" role="heading" aria-level={2}>Notifications</div>
                {unreadCount > 0 && (
                  <span className="px-1.5 py-0.5 bg-primary text-primary-foreground text-xs rounded-full">
                    {unreadCount}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1">
                {notifications.length > 0 && (
                  <>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-7 text-xs"
                      onClick={markAllAsRead}
                      aria-label="Mark all notifications as read"
                    >
                      Mark all read
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-7 px-2"
                      onClick={clearAll}
                      aria-label="Clear all notifications"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </>
                )}
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-7 px-2"
                  onClick={onClose}
                  aria-label="Close notification center"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Content */}
            <ScrollArea className="max-h-[calc(70vh-60px)] relative z-10">
              {notifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                  <Bell className="w-12 h-12 mb-3 opacity-30" />
                  <p className="text-sm">No notifications</p>
                  <p className="text-xs mt-1">You're all caught up!</p>
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {notifications.map((notification, index) => (
                    <motion.div
                      key={notification.id}
                      className={cn(
                        "flex gap-3 px-4 py-3 hover:bg-secondary/50 transition-colors cursor-pointer group",
                        !notification.read && "bg-primary/5"
                      )}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      {/* Icon */}
                      <div className="shrink-0 mt-0.5">
                        {getIcon(notification.type)}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className={cn(
                            "text-sm truncate",
                            !notification.read ? "font-medium text-foreground" : "text-foreground/80"
                          )}>
                            {notification.title}
                          </p>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              removeNotification(notification.id);
                            }}
                            aria-label="Dismiss notification"
                            className="opacity-0 group-hover:opacity-100 p-2 rounded hover:bg-secondary transition-all min-w-[32px] min-h-[32px] flex items-center justify-center"
                          >
                            <X className="w-4 h-4 text-muted-foreground" />
                          </button>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                          {notification.message}
                        </p>
                        <p className="text-[10px] text-muted-foreground/70 mt-1">
                          {notification.time}
                        </p>
                      </div>

                      {/* Unread indicator */}
                      {!notification.read && (
                        <div className="shrink-0">
                          <div className="w-2 h-2 rounded-full bg-primary" />
                        </div>
                      )}
                    </motion.div>
                  ))}
                </div>
              )}
            </ScrollArea>

            {/* Footer */}
            <div className={cn(
              "flex items-center justify-between px-4 py-2 border-t relative z-10",
              glassEnabled 
                ? "border-white/10 bg-white/5" 
                : "border-border bg-secondary/30"
            )}>
              <button className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
                <Settings className="w-3.5 h-3.5" />
                Notification settings
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
